/**
 * Integrations (Admin) — MyTowMate billing sync.
 *
 * Shows connection status + last sync, a manual "Sync now" button, and the
 * synced invoices pulled from MyTowMate (platform_invoices). Read paths are
 * cheap (no upstream call); the sync button hits the rate-limited endpoint.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, CheckCircle, XCircle, Plug } from 'lucide-react';

interface MyTowMateStatus {
  configured: boolean;
  pollerEnabled: boolean;
  counts: { open: number; overdue: number; paid: number; void: number; total: number };
  lastSyncedAt: string | null;
}

interface SyncedInvoice {
  id: string;
  sourceInvoiceId: string;
  billingMonth: string;
  tenantName: string;
  totalAmount: number | string;
  status: string;
  dueAt: string | null;
  paidAt: string | null;
  lastSyncedAt: string;
}

const statusColors: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  overdue: 'bg-red-100 text-red-800',
  paid: 'bg-green-100 text-green-800',
  void: 'bg-gray-100 text-gray-600',
};

function money(v: number | string) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return `$${(Number.isFinite(n) ? n : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const { data: statusData } = useQuery({
    queryKey: ['mytowmate-status'],
    queryFn: () => api.getMyTowMateStatus(),
    refetchInterval: 60_000,
  });
  const status = (statusData?.data || null) as MyTowMateStatus | null;

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['mytowmate-invoices', statusFilter],
    queryFn: () => api.getMyTowMateInvoices({ status: statusFilter, limit: 100 }),
  });
  const invoices = (invoicesData?.data || []) as SyncedInvoice[];

  const syncMutation = useMutation({
    mutationFn: () => api.runMyTowMateSync({}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mytowmate-status'] });
      queryClient.invalidateQueries({ queryKey: ['mytowmate-invoices'] });
      toast({ title: 'Sync complete' });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast({ title: 'Sync failed', description: msg, variant: 'destructive' });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Plug className="h-6 w-6" /> Integrations
          </h1>
          <p className="text-muted-foreground">MyTowMate billing sync</p>
        </div>
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending || !status?.configured}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? 'Syncing...' : 'Sync now'}
        </Button>
      </div>

      {/* Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">MyTowMate</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-1.5">
              {status?.configured ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              {status?.configured ? 'Connected' : 'Not configured'}
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              Daily auto-sync: {status?.pollerEnabled ? 'On' : 'Off (manual only)'}
            </span>
            <span className="text-muted-foreground">
              Last synced: {status?.lastSyncedAt ? formatDateTime(status.lastSyncedAt) : 'Never'}
            </span>
          </div>
          {status && (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="px-2 py-1 rounded bg-muted">Total: <b>{status.counts.total}</b></span>
              <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">Open: {status.counts.open}</span>
              <span className="px-2 py-1 rounded bg-red-50 text-red-700">Overdue: {status.counts.overdue}</span>
              <span className="px-2 py-1 rounded bg-green-50 text-green-700">Paid: {status.counts.paid}</span>
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">Void: {status.counts.void}</span>
            </div>
          )}
          {!status?.pollerEnabled && status?.configured && (
            <p className="text-xs text-muted-foreground">
              Auto-sync runs daily only when <code className="font-mono">ENABLE_MYTOWMATE_POLLER=true</code>. Until then, use “Sync now”.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2">
        {[undefined, 'open', 'overdue', 'paid', 'void'].map((s) => (
          <Button
            key={s ?? 'all'}
            variant={statusFilter === s ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {s ? s[0].toUpperCase() + s.slice(1) : 'All'}
          </Button>
        ))}
      </div>

      {/* Invoices */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No synced invoices{statusFilter ? ` with status “${statusFilter}”` : ''} yet.
            {status?.configured && ' Run “Sync now” after MyTowMate has open/overdue invoices.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="p-3">Tenant</th>
                  <th className="p-3">Invoice</th>
                  <th className="p-3">Month</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Due</th>
                  <th className="p-3">Synced</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="p-3 font-medium">{inv.tenantName}</td>
                    <td className="p-3 text-muted-foreground">{inv.sourceInvoiceId}</td>
                    <td className="p-3">{inv.billingMonth}</td>
                    <td className="p-3 text-right tabular-nums">{money(inv.totalAmount)}</td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusColors[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{inv.dueAt ? formatDateTime(inv.dueAt) : '—'}</td>
                    <td className="p-3 text-muted-foreground">{formatDateTime(inv.lastSyncedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
